import { useId, useRef, useState, useMemo } from "react";

const AttachmentPicker = ({
  label = "Anexos",
  hint = "Arraste e solte aqui ou clique para escolher",
  files = [],
  onChange = () => {},
  accept, // ex: "image/*,.pdf"
  maxFiles = 8,
  maxSizeMB = 25,
  compact = false, // usa altura menor (bom para edição inline)
}) => {
  const [isOver, setIsOver] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const dropId = useId();
  const helperId = `${dropId}-hint`;
  const errorId = `${dropId}-err`;

  const prettySize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0,
      n = Math.max(bytes, 0);
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i++;
    }
    return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const resetError = () => setError("");

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;

    // validações
    if (files.length + arr.length > maxFiles) {
      setError(`Máximo de ${maxFiles} arquivos.`);
      return;
    }
    const overs = arr.filter((f) => f.size > maxSizeMB * 1024 * 1024);
    if (overs.length) {
      setError(`Cada arquivo deve ter até ${maxSizeMB}MB.`);
      return;
    }

    // ok
    resetError();
    onChange([...files, ...arr]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };

  const onPaste = (e) => {
    // permite colar imagem do clipboard (prints)
    const items = e.clipboardData?.items || [];
    const filesFromPaste = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) filesFromPaste.push(f);
      }
    }
    if (filesFromPaste.length) addFiles(filesFromPaste);
  };

  const removeAt = (idx) => {
    resetError();
    const next = [...files];
    next.splice(idx, 1);
    onChange(next);
  };

  const clearAll = () => {
    resetError();
    onChange([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  // memo para evitar recriar URL a cada render
  const thumbs = useMemo(() => {
    return files.map((f) => ({
      isImg: /^image\//.test(f.type),
      name: f.name,
      size: f.size,
      url: /^image\//.test(f.type) ? URL.createObjectURL(f) : null,
    }));
  }, [files]);

  // revoga URLs ao desmontar/mudar
  // (opcional; os onLoad abaixo já ajudam também)
  // useEffect(() => () => thumbs.forEach(t => t.url && URL.revokeObjectURL(t.url)), [thumbs]);

  return (
    <div className="w-full mt-4" onPaste={onPaste}>
      {label && (
        <label
          htmlFor={dropId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
      )}

      <div
        id={dropId}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={[
          "rounded-2xl border-2 border-dashed transition-all cursor-pointer bg-white focus-within:ring-2 focus-within:ring-offset-1 focus-within:ring-gray-300",
          compact ? "p-2" : "p-2",
          isOver
            ? "border-gray-800 bg-gray-50"
            : "border-gray-300 hover:border-gray-400",
        ].join(" ")}
        role="button"
        tabIndex={0}
        aria-describedby={[helperId, error ? errorId : null]
          .filter(Boolean)
          .join(" ")}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && inputRef.current?.click()
        }
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-7 h-7 text-xs rounded-xl bg-gray-100 text-gray-600">
            <span aria-hidden>📎</span>
          </div>

          <div className="min-w-0">
            <div className="text-xs font-semibold text-gray-900">{hint}</div>
            <div id={helperId} className="text-[11px] text-gray-500">
              {`Até ${maxFiles} arquivos • máx ${maxSizeMB}MB cada`}
            </div>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {/* Mensagem de erro */}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}

      {/* Lista de arquivos */}
      {files?.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-xs text-gray-600">
              {files.length} arquivo{files.length > 1 ? "s" : ""}
              {files.length > 1 && (
                <>
                  {" "}
                  • {prettySize(files.reduce((a, f) => a + (f.size || 0), 0))}
                </>
              )}
            </span>
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-700 underline"
              onClick={clearAll}
            >
              Limpar tudo
            </button>
          </div>

          <ul className="flex flex-wrap gap-2">
            {thumbs.map((t, idx) => (
              <li
                key={`${t.name}-${idx}`}
                className="group flex items-center gap-2 max-w-full rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-sm"
              >
                <div className="h-8 w-8 rounded-md bg-gray-100 grid place-items-center overflow-hidden">
                  {t.isImg ? (
                    <img
                      src={t.url}
                      alt={t.name}
                      className="h-full w-full object-cover"
                      onLoad={() => t.url && URL.revokeObjectURL(t.url)}
                    />
                  ) : (
                    <span className="text-gray-600" aria-hidden>
                      📄
                    </span>
                  )}
                </div>

                <div className="min-w-0">
                  <div className="truncate max-w-[160px] sm:max-w-[220px] text-gray-900">
                    {t.name}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {prettySize(t.size)}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAt(idx);
                  }}
                  className="ml-1 rounded-md border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50"
                  title="Remover"
                >
                  Remover
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AttachmentPicker;
